import { FC, ReactNode } from 'react'
import { useTar } from '@/modules/i18n'

const CodeBlock: FC<{ children: ReactNode; multiline?: boolean }> = ({
  children,
  multiline,
}) => (
  <code
    className={`block bg-card p-2 rounded-lg border border-border text-xs text-primary font-bold ${multiline ? 'whitespace-pre-wrap' : ''}`}
  >
    {children}
  </code>
)

export const SqlCheatSheetTab: FC = () => {
  const { t } = useTar()

  return (
    <div className="p-4 lg:p-6 min-h-full">
      <div className="mb-4">
        <h2 className="text-2xl font-semibold">{t('sqlCheatSheet.title')}</h2>
        <p className="text-muted-foreground mt-1">{t('sqlCheatSheet.description')}</p>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Queries */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-accent border-b border-border pb-2">
              {t('sqlCheatSheet.basicQueries')}
            </h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">SELECT</h4>
                <CodeBlock>SELECT column1, column2 FROM table_name;</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">WHERE</h4>
                <CodeBlock>{`SELECT * FROM users WHERE age > 18;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">ORDER BY</h4>
                <CodeBlock>SELECT * FROM products ORDER BY price DESC;</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">LIMIT</h4>
                <CodeBlock>SELECT * FROM posts LIMIT 10;</CodeBlock>
              </div>
            </div>
          </div>

          {/* Joins */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-accent border-b border-border pb-2">
              {t('sqlCheatSheet.joins')}
            </h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">INNER JOIN</h4>
                <CodeBlock multiline>{`SELECT u.name, p.title FROM users u
INNER JOIN posts p ON u.id = p.user_id;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">LEFT JOIN</h4>
                <CodeBlock multiline>{`SELECT u.name, p.title FROM users u
LEFT JOIN posts p ON u.id = p.user_id;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">RIGHT JOIN</h4>
                <CodeBlock multiline>{`SELECT u.name, p.title FROM users u
RIGHT JOIN posts p ON u.id = p.user_id;`}</CodeBlock>
              </div>
            </div>
          </div>

          {/* Aggregation */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-warning border-b border-border pb-2">
              {t('sqlCheatSheet.aggregation')}
            </h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">GROUP BY</h4>
                <CodeBlock multiline>{`SELECT category, COUNT(*) FROM products
GROUP BY category;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">HAVING</h4>
                <CodeBlock multiline>{`SELECT category, COUNT(*) FROM products
GROUP BY category HAVING COUNT(*) > 5;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">{t('sqlCheatSheet.functions')}</h4>
                <CodeBlock multiline>{`SELECT AVG(price), MAX(price), MIN(price)
FROM products;`}</CodeBlock>
              </div>
            </div>
          </div>

          {/* Advanced Features */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-accent border-b border-border pb-2">
              {t('sqlCheatSheet.advancedFeatures')}
            </h3>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">{t('sqlCheatSheet.subqueries')}</h4>
                <CodeBlock multiline>{`SELECT * FROM products WHERE price >
(SELECT AVG(price) FROM products);`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">{t('sqlCheatSheet.windowFunctions')}</h4>
                <CodeBlock multiline>{`SELECT name, salary,
ROW_NUMBER() OVER (ORDER BY salary DESC)
FROM employees;`}</CodeBlock>
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">{t('sqlCheatSheet.ctes')}</h4>
                <CodeBlock multiline>{`WITH high_salary AS (
  SELECT * FROM employees
  WHERE salary > 50000
) SELECT * FROM high_salary;`}</CodeBlock>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
